import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { BackButton, PrimaryButton, SecondaryButton } from 'components/forms/atoms/FormButtons';
import FormField from 'components/forms/atoms/FormField';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import PageTitle from 'components/UI/PageTitle';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { type FC, useEffect, useState } from 'react';
import type { DbOutput } from 'services/api/admin-db.api';
import {
  useAdminExecuteQueryMutation,
  useAdminGenerateAIQueryMutation,
  useAdminGenerateResultsAnalysisMutation,
} from 'services/api/admin-db.api';
import {
  useCreateFavoriteQueryMutation,
  useDeleteFavoriteQueryMutation,
  useGetFavoriteQueriesQuery,
} from 'services/api/favoriteQueries.api';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SHADOW_CARD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
} from 'styles/tokens';

import GenerateChartToolComponent from './components/GenerateChartToolComponent';

const ERROR_RED = '#E7233B';
const LINK_BLUE = '#2C5889';

const glassDialogPaperSx = {
  backdropFilter: GLASS_BLUR,
  backgroundColor: GLASS_BACKGROUND,
  boxShadow: SHADOW,
  borderRadius: RADIUS_CARD,
  border: `1px solid ${alpha(NAVY, 0.06)}`,
};

const cardSx = {
  bgcolor: SURFACE_CONTAINER_LOWEST,
  borderRadius: RADIUS_CARD,
  boxShadow: SHADOW,
  p: 3,
};

const mutedTextSx = {
  fontFamily: 'Jost, sans-serif',
  fontSize: '0.8125rem',
  color: alpha(NAVY, 0.6),
};

const sectionTitleSx = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 600,
  color: NAVY,
};

const buttonRectSx = { borderRadius: RADIUS_ITEM };

const DescriptionComponent: FC<{ args: string }> = ({ args }) => {
  const { summary, conclusion } = JSON.parse(args) as { summary?: string; conclusion?: string };
  return (
    <Box>
      {summary && (
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY }}>
          <strong>Resumen:</strong> {summary}
        </Typography>
      )}
      {conclusion && (
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY }}>
          <strong>Conclusión:</strong> {conclusion}
        </Typography>
      )}
    </Box>
  );
};

const CellComponent: FC<{ value: unknown }> = (props: { value: unknown }) => {
  const { value } = props;
  if (typeof value === 'string' && value.startsWith('http')) {
    return (
      <a href={value} target="_blank" rel="noreferrer" style={{ color: LINK_BLUE }}>
        {value}
      </a>
    );
  }
  if (typeof value === 'object') {
    return <div>{JSON.stringify(value)}</div>;
  }
  if (typeof value === 'boolean') {
    return <div>{value ? 'true' : 'false'}</div>;
  }
  return <div>{String(value)}</div>;
};

const FavoriteQueryCard: FC<{
  title: string;
  query: string;
  id: number;
  onSelect: (query: string) => void;
  onDelete: (id: number) => void;
}> = ({ title, query, id, onSelect, onDelete }) => {
  return (
    <Box
      sx={{
        bgcolor: SURFACE_CONTAINER_LOWEST,
        borderRadius: RADIUS_CARD,
        boxShadow: SHADOW_CARD,
        p: 2,
        mb: 2,
        cursor: 'pointer',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
        '&:hover': {
          backgroundColor: SURFACE_HOVER,
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
        },
      }}
    >
      <Box onClick={() => onSelect(query)}>
        <Typography variant="h6" gutterBottom sx={sectionTitleSx}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.7) }}
        >
          {query}
        </Typography>
      </Box>
      <IconButton
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: alpha(NAVY, 0.5),
          '&:hover': { color: ERROR_RED, backgroundColor: alpha(ERROR_RED, 0.08) },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(id);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const DbReader = () => {
  const [userQuery, setUserQuery] = useState<string>('');
  const [sqlQuery, setSQLQuery] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [favoriteTitle, setFavoriteTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null);

  const { notifySaved } = useFeedback();

  const [generateAIQuery, { data: aiQueryData, isLoading }] = useAdminGenerateAIQueryMutation();
  const [executeQuery, { data: queryData, error, isLoading: isLoadingResult }] = useAdminExecuteQueryMutation();
  const { data: favoriteQueries, refetch: refetchFavoriteQueries } = useGetFavoriteQueriesQuery(null);
  const [createFavoriteQuery] = useCreateFavoriteQueryMutation();
  const [deleteFavoriteQuery] = useDeleteFavoriteQueryMutation();
  const [generateResultsAnalysis, { data: resultsAnalysisData, isLoading: isLoadingResultsAnalysis }] =
    useAdminGenerateResultsAnalysisMutation();

  const handleGenerateAIQuery = () => {
    generateAIQuery({ userQuery }).catch((err) => console.error(err));
  };

  const handleExecuteQuery = () => {
    executeQuery({ userQuery: sqlQuery }).catch((err) => console.error(err));
  };

  const handleSelectFavoriteQuery = (query: string) => {
    setSQLQuery(query);
  };

  const handleSaveAsFavorite = async () => {
    await createFavoriteQuery({
      title: favoriteTitle,
      query: sqlQuery,
    });
    setIsModalOpen(false);
    setFavoriteTitle('');
    notifySaved('Consulta guardada');
    refetchFavoriteQueries().catch((err) => console.error(err));
  };

  const handleDeleteQuery = (id: number) => {
    setSelectedQueryId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedQueryId) {
      await deleteFavoriteQuery(selectedQueryId);
      refetchFavoriteQueries().catch((err) => console.error(err));
    }
    setDeleteDialogOpen(false);
    setSelectedQueryId(null);
  };

  useEffect(() => {
    if (aiQueryData) {
      setSQLQuery(aiQueryData.sqlQuery);
      setDescription(aiQueryData.description);
    }
  }, [aiQueryData]);

  useEffect(() => {
    if (queryData?.results && queryData.results.length > 1 && queryData.results.length < 100) {
      generateResultsAnalysis({ userQuery, sql: sqlQuery, results: queryData.results }).catch((err) =>
        console.error(err)
      );
    }
  }, [queryData, userQuery, sqlQuery, generateResultsAnalysis]);

  const downloadDataAsCSV = () => {
    if (queryData?.results) {
      let fileData = `${Object.keys(queryData.results[0]).join(';')}\n`;
      fileData += queryData.results
        .map((row) =>
          Object.values(row)
            .map((value) => (typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)))
            .join(';')
        )
        .join('\n');
      const blob = new Blob([fileData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'db-data.csv';
      link.href = url;
      link.click();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission if inside a form
      handleGenerateAIQuery();
    }
  };

  const rows =
    queryData?.results?.map((data, id) => ({
      ...data,
      id: data.id && typeof data.id === 'number' ? `${id}: ${data.id}` : id,
    })) ?? [];
  const columns =
    queryData?.results && queryData.results.length > 0
      ? Object.keys(queryData.results[0]).map(
          (key): GridColDef<DbOutput> => ({
            field: key,
            headerName: key,
            // Min width in characters: max(10, max length of the column). Max width: 80. Approx 10 px per character
            width:
              12 *
              Math.min(
                Math.max(
                  10,
                  ...queryData.results.map(
                    (row) =>
                      (typeof row[key] === 'object'
                        ? JSON.stringify(row[key])
                        : (row[key] as string | number)?.toString()
                      )?.length || 0
                  )
                ),
                80
              ),
            renderCell: (params: GridRenderCellParams<DbOutput, string>) => <CellComponent value={params.value} />,
            type: 'string',
          })
        )
      : [];

  return (
    <Box sx={{ px: { xs: 2, lg: 4 }, pt: 1, pb: 3 }}>
      <PageTitle subtitle="Consulta la base de datos en lenguaje natural o con SQL." sx={{ mt: 0 }}>
        Analizador de Base de Datos
      </PageTitle>

      <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormField
          autoFocus
          fullWidth
          value={userQuery}
          onKeyPress={handleKeyPress}
          onChange={(e) => setUserQuery(e.target.value)}
          hint="Escribe lo que quieres preguntarle a la base de datos"
          label="Consulta en español"
        />
        <Box>
          <PrimaryButton onClick={handleGenerateAIQuery} disabled={isLoading || !userQuery} sx={buttonRectSx}>
            {isLoading ? 'Generando…' : 'Generar consulta'}
          </PrimaryButton>
        </Box>
        <FormField
          fullWidth
          value={sqlQuery}
          minRows={5}
          maxRows={12}
          multiline
          onChange={(e) => setSQLQuery(e.target.value)}
          hint="Escribe la consulta que quieres ejecutar"
          label="Consulta SQL"
        />
        {description && <Typography sx={mutedTextSx}>{description}</Typography>}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <PrimaryButton onClick={handleExecuteQuery} disabled={!sqlQuery || isLoadingResult} sx={buttonRectSx}>
            {isLoadingResult ? 'Ejecutando…' : 'Ejecutar consulta'}
          </PrimaryButton>
          <SecondaryButton
            onClick={() => setIsModalOpen(true)}
            disabled={!sqlQuery || isLoadingResult}
            sx={buttonRectSx}
          >
            Guardar como favorita
          </SecondaryButton>
        </Box>
        {error && (
          <InlineFeedback severity="error">
            Error al ejecutar la consulta. Revisa las comillas en los nombres de tablas y columnas, y que las columnas
            existan.
          </InlineFeedback>
        )}
      </Box>

      {isLoadingResultsAnalysis && (
        <Typography sx={{ ...mutedTextSx, mt: 2 }}>Cargando análisis de resultados...</Typography>
      )}
      {resultsAnalysisData && resultsAnalysisData.length > 0 && (
        <Box sx={{ ...cardSx, mt: 3 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            Análisis de resultados
          </Typography>
          {resultsAnalysisData.map((toolCall) => (
            <Box
              key={toolCall.id}
              sx={{
                mt: 1,
              }}
            >
              {toolCall.function.name === 'generateChart' ? (
                <GenerateChartToolComponent args={toolCall.function.arguments} size="large" />
              ) : (
                <DescriptionComponent args={toolCall.function.arguments} />
              )}
            </Box>
          ))}
        </Box>
      )}
      {queryData?.results ? (
        <Box sx={{ ...cardSx, mt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              pb: 2,
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box>
              <Typography variant="h6" sx={sectionTitleSx}>
                Resultados
              </Typography>
              {queryData.time !== null && (
                <Typography variant="body2" sx={mutedTextSx}>
                  Tiempo de ejecución: {queryData.time.toFixed(2)} ms | Cantidad de resultados:{' '}
                  {queryData.results.length}
                </Typography>
              )}
            </Box>
            <BackButton onClick={downloadDataAsCSV} sx={buttonRectSx}>
              Descargar como CSV
            </BackButton>
          </Box>
          <DataGrid
            rows={rows}
            columns={columns}
            sx={{
              border: 'none',
              fontFamily: 'Jost, sans-serif',
              color: NAVY,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: SURFACE_CONTAINER_LOW,
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
              },
              '& .MuiDataGrid-cell': {
                fontFamily: 'Jost, sans-serif',
                fontSize: '14px',
                borderBottom: 'none',
                color: NAVY,
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: SURFACE_HOVER,
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: 'none',
              },
            }}
          />
        </Box>
      ) : (
        <Box sx={{ pt: 3 }}>
          <Typography variant="h6" gutterBottom sx={sectionTitleSx}>
            Consultas guardadas
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2,
              width: '100%',
            }}
          >
            {favoriteQueries?.map((query) => (
              <FavoriteQueryCard
                key={query.id}
                id={query.id}
                title={query.title}
                query={query.query}
                onSelect={handleSelectFavoriteQuery}
                onDelete={handleDeleteQuery}
              />
            ))}
          </Box>
        </Box>
      )}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        slotProps={{ paper: { sx: glassDialogPaperSx } }}
      >
        <DialogTitle sx={sectionTitleSx}>Guardar consulta como favorita</DialogTitle>
        <DialogContent>
          <FormField
            autoFocus
            label="Título de la consulta"
            fullWidth
            value={favoriteTitle}
            onChange={(e) => setFavoriteTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <BackButton onClick={() => setIsModalOpen(false)} sx={buttonRectSx}>
            Cancelar
          </BackButton>
          <PrimaryButton
            onClick={() => {
              handleSaveAsFavorite().catch((err) => console.error(err));
            }}
            disabled={!favoriteTitle}
            sx={buttonRectSx}
          >
            Guardar
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        slotProps={{ paper: { sx: glassDialogPaperSx } }}
      >
        <DialogTitle sx={sectionTitleSx}>¿Estás seguro?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <BackButton onClick={() => setDeleteDialogOpen(false)} sx={buttonRectSx}>
            Cancelar
          </BackButton>
          <SecondaryButton
            onClick={() => {
              confirmDelete().catch((err) => console.error(err));
            }}
            sx={{
              ...buttonRectSx,
              bgcolor: ERROR_RED,
              '&:hover': { bgcolor: ERROR_RED, boxShadow: `0 8px 20px ${alpha(ERROR_RED, 0.3)}` },
            }}
          >
            Eliminar
          </SecondaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DbReader;
